/**
 * Reading a set out of a search.
 *
 * "Umbreon prismatic" and "time of battle luffy" are the same problem: people
 * do not hold a card name, they hold a card, and the set is printed on it — so
 * they type it, and a search that only matches names finds nothing.
 *
 * Shared because both games need it and the rule is not game-specific. What is
 * game-specific stays outside: Pokemon reads 218 set names fetched from tcgdex
 * and can afford a fuzzy pass over them on the server; One Piece has 22 sets
 * and searches in the browser, so it gets the exact pass and no bundled
 * matcher. That difference is the `near` argument and nothing else.
 */

export const wordsOf = (s) =>
  String(s || "").toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);

/* Two letters is not enough to mean a set: "ex" would claim EX Ruby & Sapphire
   off every "Charizard ex" typed. Digits are exempt because 151 is a set. */
export const setToken = (t) => t.length >= 3 || /^\d+$/.test(t);

/**
 * Whether a run of words is worth testing against the set list at all.
 *
 * The rule has to be about the run rather than each word in it. Real set names
 * are full of two-letter joins — The Time of Battle, Kingdoms of Intrigue,
 * 500 Years in the Future — and asking every word to carry three letters threw
 * all of those away for the sake of "of".
 *
 * A single word still has to stand on its own, which is what keeps "ex" from
 * claiming EX Ruby & Sapphire off every "Charizard ex" typed. Several words
 * only need substance between them, because matching a multi-word set name is
 * already evidence enough on its own.
 */
export const usableRun = (run) =>
  run.length === 1
    ? setToken(run[0])
    : run.some((t) => t.length >= 3) && run.join("").length >= 4;

/* Sources disagree about leading zeros, and set codes carry letters in front
   of the digits — "OP-16" and "op16" are the same set. */
export const flatCode = (s) =>
  String(s || "").toLowerCase().replace(/[^a-z0-9]/g, "").replace(/^([a-z]*)0+(?=\d)/, "$1");

/**
 * Which sets, if any, a run of words names.
 *
 * Every word must begin a different word of the set's name, so "trainer
 * gallery" reaches all six Trainer Gallery sets while "gallery gallery"
 * reaches none. Ties go to the tightest name: "151" is the set called 151
 * rather than every set with a 151 in it.
 *
 * A single word is also tried as a set code, because that is how people say a
 * One Piece set out loud and increasingly how they say a Pokemon one.
 */
export function setsNamed(run, index) {
  if (run.length === 1) {
    const asked = flatCode(run[0]);
    const byCode = Object.keys(index).filter((id) => flatCode(id) === asked);
    if (byCode.length) return byCode;
  }

  let best = Infinity;
  let found = [];
  for (const [id, set] of Object.entries(index)) {
    const ws = wordsOf(set.name);
    const used = new Set();
    let ok = true;
    for (const t of run) {
      const at = ws.findIndex((w, i) => !used.has(i) && w.startsWith(t));
      if (at < 0) { ok = false; break; }
      used.add(at);
    }
    if (!ok) continue;
    const extra = ws.length - run.length;      // words of the set name unclaimed
    if (extra < best) { best = extra; found = [id]; }
    else if (extra === best) found.push(id);
  }
  return found;
}

/**
 * Split a query into the set it names and the name it searches.
 *
 * Every candidate is returned rather than the first, because a run that names
 * a set is not always the run that was meant: "151 pikachu" reads "pikachu" as
 * Detective Pikachu, which is a real set, and then searches it for a card
 * called 151. The caller tries them in order and keeps the one that finds
 * cards — the only test of a reading that actually settles it.
 *
 * @param near optional (run) => setIds, a fuzzy pass tried only after every
 *             exact reading has been offered, so a misspelling can never
 *             outrank a set the query actually named.
 */
export function splitSetPhrase(term, index, { near } = {}) {
  const q = wordsOf(term);
  if (!q.length || !index) return [];

  const found = [];
  const seen = new Set();
  const take = (run, start, sets) => {
    const key = `${start}:${run.length}:${sets[0]}`;
    if (seen.has(key)) return;
    seen.add(key);
    found.push({
      sets,
      setName: index[sets[0]]?.name || sets[0],
      rest: [...q.slice(0, start), ...q.slice(start + run.length)].join(" "),
    });
  };

  for (const match of near ? [setsNamed, near] : [setsNamed]) {
    for (let len = q.length; len >= 1; len--) {
      for (let start = q.length - len; start >= 0; start--) {
        const run = q.slice(start, start + len);
        if (!usableRun(run)) continue;
        const sets = match(run, index);
        if (sets.length) take(run, start, sets);
      }
    }
  }
  return found;
}
